import {
  Combobox,
  ComboboxTrigger,
  ComboboxContent,
  ComboboxInput,
  ComboboxList,
  ComboboxListItem,
  ComboboxEmpty,
} from "@design-system/shadcn";
import React, { useCallback, useMemo } from "react";

import { useFieldContext } from "../core/form-contexts";
import { useFieldDescribedBy } from "../core/use-field-described-by";

type FormFieldComboboxProps = Omit<
  React.ComponentProps<typeof Combobox>,
  "value" | "onChange"
>;

function FormFieldCombobox({ children, ...props }: FormFieldComboboxProps) {
  const field = useFieldContext<string>();

  if (!field) {
    throw new Error("FormFieldCombobox must be used within form.AppField");
  }

  const changeHandler = useCallback(
    (value: string) => field.handleChange(value),
    [field]
  );

  return (
    <Combobox value={field.state.value} onChange={changeHandler} {...props}>
      {children}
    </Combobox>
  );
}

FormFieldCombobox.Trigger = FormFieldComboboxTrigger;
FormFieldCombobox.Content = ComboboxContent;
FormFieldCombobox.Input = ComboboxInput;
FormFieldCombobox.List = ComboboxList;
FormFieldCombobox.ListItem = ComboboxListItem;
FormFieldCombobox.Empty = ComboboxEmpty;

type FormFieldComboboxTriggerProps = Omit<
  React.ComponentProps<typeof ComboboxTrigger>,
  "hasError"
>;

function FormFieldComboboxTrigger({
  children,
  ...props
}: FormFieldComboboxTriggerProps) {
  const field = useFieldContext<string>();

  if (!field) {
    throw new Error(
      "FormFieldComboboxTrigger must be used within form.AppField"
    );
  }

  const hasError = useMemo(
    () => field.state.meta.errors.length > 0,
    [field.state.meta.errors.length]
  );

  const describedBy = useFieldDescribedBy(props["aria-describedby"]);

  const labelId = `${field.name}-label`;

  return (
    <ComboboxTrigger
      id={field.name}
      aria-invalid={hasError}
      aria-labelledby={labelId}
      {...props}
      aria-describedby={describedBy}
    >
      {children}
    </ComboboxTrigger>
  );
}

export { FormFieldCombobox, FormFieldComboboxTrigger };
export type { FormFieldComboboxProps, FormFieldComboboxTriggerProps };
